import React from "react";
import {
    Box,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    useTheme,
    Container,
    Stack,
    Divider
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { tokens } from "../../theme";
import { hoverLift, eyebrow, sectionPadding } from "../../styles/surfaces";
import Reveal from "../../components/Reveal";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import { Link } from "react-router-dom";
import curtainWallingIcon from "../../images/icons/curtain-walling-installation.png";
import claddingIcon from "../../images/icons/cladding-installation.png";
import remediationIcon from "../../images/icons/fire-remediation.png";

const About = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const stats = [
        { prefix: "Established in", highlight: "2018" },
        { number: "100%", label: "Client Satisfaction" },
        { number: "30+", label: "Team Members" }
    ];

    const whoWeAreParagraphs = [
        "Established in 2018, AV Facade Services Ltd is a specialist labour-only facade installation contractor delivering high-quality facade solutions across the UK. We have built a strong reputation by working alongside leading main contractors, successfully completing projects across the commercial, residential, education, healthcare, and mixed-use sectors.",
        "Our expertise covers the installation of curtain walling, aluminium windows and doors, cladding, and facade remedial works. From large-scale new-build developments to complex refurbishment projects, we provide skilled installation teams committed to delivering every project safely, efficiently, and to the highest standard.",
        "At AV Facade Services, we understand that every project is unique. That's why we work closely with our clients, adapting to programme requirements while maintaining exceptional attention to detail, quality, and compliance with industry standards. Our experienced operatives are dedicated to achieving outstanding results, ensuring installations are completed with precision, reliability, and professionalism.",
        "Since our establishment, we have successfully delivered numerous façade projects across the UK, earning the trust of contractors through our consistent workmanship, strong work ethic, and commitment to health and safety. We take pride in building lasting relationships with our clients by delivering dependable labour solutions that keep projects moving forward.",
        "As our business continues to grow, our focus remains unchanged: providing expert façade installation services backed by skilled professionals, transparent communication, and an unwavering commitment to quality on every project.",
    ];

    const services = [
        {
            icon: curtainWallingIcon,
            title: "Curtain wall",
            description: "Key Services",
            features: [
                "Schüco, Kawneer, Technal, Metal Technology, SAS, Aluprof & SAPA systems",
                "Stick and unitised curtain walling",
                "New-build and refurbishment projects",
                "Precision installation and quality assurance"
            ]
        },
        {
            icon: claddingIcon,
            title: "Cladding",
            description: "Key Services",
            features: [
                "Brick Slip, Terracotta & Hardie® Plank systems",
                "Ash & Lacy, Trespa, Parklex, Euroclad & Eurobond systems",
                "New-build and refurbishment projects",
                "Precision installation and quality assurance"
            ]
        },
        {
            icon: remediationIcon,
            title: "Remediation",
            description: "Key Services",
            features: [
                "Façade remediation & replacement works",
                "Cladding, glazing & façade component replacement",
                "EPDM, insulation & breather membrane installation",
                "Building safety compliance & quality assurance"
            ]
        }
    ];

    return (
        <Box>
            {/* Hero Section */}
            {/* `background-attachment: fixed` used to sit here for a parallax effect. iOS
                Safari does not composite fixed backgrounds with scrolling, so it juddered
                on exactly the devices that see it most; the scrim does the work instead. */}
            <Box
                sx={{
                    background: `linear-gradient(180deg, rgba(8,12,20,.74) 0%, rgba(8,12,20,.48) 45%, rgba(8,12,20,.8) 100%), url('https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1920')`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    py: { xs: 9, md: 14 },
                    textAlign: "center",
                    color: "white"
                }}
            >
                <Container maxWidth="lg">
                    <Typography
                        variant="h1"
                        sx={{ mb: 2.5 }}
                    >
                        About Us
                    </Typography>
                    <Typography
                        sx={{
                            mb: 4,
                            maxWidth: '620px',
                            mx: 'auto',
                            fontSize: { xs: '1.0625rem', md: '1.25rem' },
                            lineHeight: 1.6,
                            color: alpha('#ffffff', 0.88)
                        }}
                    >
                        Building Excellence. Delivering Precision. Earning Trust.
                    </Typography>

                    {/* Stats Section */}
                    <Grid container spacing={4} sx={{ mt: 4 }} justifyContent="center" alignItems="stretch">
                        {stats.map((stat, index) => (
                            <Grid item xs={12} sm={4} key={index} sx={{ display: 'flex' }}>
                                <Box
                                    sx={{
                                        p: 3,
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minHeight: 120,
                                        // Frosted glass over the photograph rather than a
                                        // flat black panel, with a lit top edge so the
                                        // tile has a shape of its own.
                                        backgroundColor: alpha('#0b1018', 0.42),
                                        border: `1px solid ${alpha('#ffffff', 0.16)}`,
                                        borderRadius: 3,
                                        backdropFilter: 'blur(14px)',
                                        WebkitBackdropFilter: 'blur(14px)',
                                    }}
                                >
                                    {stat.prefix ? (
                                        <>
                                            <Typography
                                                variant="h3"
                                                sx={{
                                                    color: theme.palette.primary.light,
                                                    mb: 0.5,
                                                }}
                                            >
                                                {stat.highlight}
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                sx={{ color: alpha('#ffffff', 0.82) }}
                                            >
                                                {stat.prefix}
                                            </Typography>
                                        </>
                                    ) : (
                                        <>
                                            <Typography
                                                variant="h3"
                                                sx={{
                                                    color: theme.palette.primary.light,
                                                    mb: 0.5
                                                }}
                                            >
                                                {stat.number}
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                sx={{ color: alpha('#ffffff', 0.82) }}
                                            >
                                                {stat.label}
                                            </Typography>
                                        </>
                                    )}
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Main Content */}
            <Box sx={{ backgroundColor: colors.background[200], ...sectionPadding }}>
                <Container maxWidth="lg">
                    {/* Services Section */}
                    <Grid container spacing={4} sx={{ mb: { xs: 8, md: 11 } }}>
                        {services.map((service, index) => (
                            <Grid item xs={12} md={4} key={index}>
                                <Reveal index={index} sx={{ height: '100%' }}>
                                    <Card
                                        sx={{
                                            height: '100%',
                                            backgroundColor: colors.background[300],
                                            ...hoverLift(theme)
                                        }}
                                    >
                                        <CardContent sx={{ p: { xs: 3, md: 3.5 } }}>
                                            <Stack spacing={3}>
                                                <Box sx={{ textAlign: 'center', mb: 1 }}>
                                                    {/* The illustrations are navy line art on an opaque
                                                        white background, so they need a light circular
                                                        chip to read as a badge rather than a white
                                                        square. background[100] is the lightest step of
                                                        the ramp — pure #ffffff would be the only true
                                                        white left anywhere on the light theme. */}
                                                    <Box
                                                        component="img"
                                                        src={service.icon}
                                                        alt=""
                                                        aria-hidden="true"
                                                        sx={{
                                                            display: 'block',
                                                            mx: 'auto',
                                                            width: { xs: 64, md: 76 },
                                                            height: { xs: 64, md: 76 },
                                                            borderRadius: '50%',
                                                            backgroundColor: tokens("light").background[100],
                                                            border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
                                                        }}
                                                    />
                                                    <Typography
                                                        variant="h5"
                                                        sx={{
                                                            mt: 2,
                                                            color: theme.palette.text.primary,
                                                        }}
                                                    >
                                                        {service.title}
                                                    </Typography>
                                                </Box>

                                                <Typography
                                                    component="span"
                                                    sx={{ ...eyebrow(theme), mb: 0, textAlign: 'center' }}
                                                >
                                                    {service.description}
                                                </Typography>

                                                <Divider sx={{ borderColor: theme.palette.divider }} />

                                                <Stack spacing={1.5}>
                                                    {service.features.map((feature, idx) => (
                                                        <Box
                                                            key={idx}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'flex-start',
                                                                gap: 1.25
                                                            }}
                                                        >
                                                            <CheckCircleIcon
                                                                sx={{
                                                                    color: theme.palette.primary.main,
                                                                    fontSize: 20,
                                                                    mt: '2px',
                                                                    flexShrink: 0
                                                                }}
                                                            />
                                                            <Typography
                                                                variant="body2"
                                                                sx={{ color: theme.palette.text.secondary }}
                                                            >
                                                                {feature}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Stack>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Reveal>
                            </Grid>
                        ))}
                    </Grid>

                    {/* Who We Are Section */}
                    <Box sx={{ mb: { xs: 8, md: 11 }, textAlign: 'center' }}>
                        <Reveal>
                            <Typography
                                variant="h2"
                                sx={{ color: theme.palette.text.primary }}
                            >
                                Who We Are
                            </Typography>
                            <Box
                                sx={{
                                    width: 56,
                                    height: 3,
                                    borderRadius: 2,
                                    mx: 'auto',
                                    mt: 2.5,
                                    mb: 5,
                                    backgroundColor: theme.palette.primary.main,
                                }}
                            />
                        </Reveal>
                        <Stack spacing={3} sx={{ maxWidth: '68ch', margin: '0 auto' }}>
                            {whoWeAreParagraphs.map((paragraph, index) => (
                                <Typography
                                    key={index}
                                    variant="body1"
                                    sx={{
                                        fontSize: '1.0625rem',
                                        color: theme.palette.text.secondary,
                                        lineHeight: 1.8,
                                        textAlign: 'left'
                                    }}
                                >
                                    {paragraph}
                                </Typography>
                            ))}
                        </Stack>
                        <Reveal>
                            <Box
                                sx={{
                                    position: 'relative',
                                    mt: 6,
                                    mx: 'auto',
                                    maxWidth: '760px',
                                    px: { xs: 3, md: 6 },
                                    py: { xs: 4, md: 5 },
                                    backgroundColor: colors.background[300],
                                    border: `1px solid ${colors.background[400]}`,
                                    borderRadius: 4,
                                    overflow: 'hidden',
                                    textAlign: 'left',
                                }}
                            >
                                {/* The 4px left rule became an oversized watermark glyph —
                                    the quote now looks quoted rather than merely indented. */}
                                <FormatQuoteIcon
                                    aria-hidden="true"
                                    sx={{
                                        position: 'absolute',
                                        top: -14,
                                        right: 8,
                                        fontSize: 130,
                                        color: alpha(theme.palette.primary.main, 0.1),
                                        pointerEvents: 'none',
                                    }}
                                />
                                <Typography
                                    component="blockquote"
                                    sx={{
                                        position: 'relative',
                                        m: 0,
                                        fontSize: { xs: '1.125rem', md: '1.375rem' },
                                        fontWeight: 600,
                                        fontStyle: 'italic',
                                        color: theme.palette.text.primary,
                                        lineHeight: 1.6,
                                        letterSpacing: '-0.01em',
                                    }}
                                >
                                    "Our reputation is built on precision, reliability, and delivering quality facade installations that stand the test of time."
                                </Typography>
                            </Box>
                        </Reveal>
                    </Box>

                    {/* CTA Section */}
                    <Reveal>
                        <Box
                            sx={{
                                p: { xs: 4, md: 7 },
                                textAlign: 'center',
                                backgroundColor: theme.palette.primary.dark,
                                borderRadius: 5,
                                position: 'relative',
                                overflow: 'hidden',
                                color: theme.palette.common.white,
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    inset: 0,
                                    background: `radial-gradient(120% 90% at 15% 0%, ${alpha('#ffffff', 0.18)} 0%, transparent 60%)`,
                                    pointerEvents: 'none',
                                },
                            }}
                        >
                            <Box sx={{ position: 'relative' }}>
                                <Typography
                                    variant="h3"
                                    sx={{
                                        mb: 2,
                                        color: 'inherit',
                                    }}
                                >
                                    Ready to Start Your Project?
                                </Typography>
                                <Typography
                                    sx={{
                                        mb: 4,
                                        color: alpha('#ffffff', 0.88),
                                        fontSize: { xs: '1.0625rem', md: '1.1875rem' },
                                        maxWidth: '600px',
                                        mx: 'auto'
                                    }}
                                >
                                    Contact us today for a free consultation and estimate.
                                </Typography>
                                <Button
                                    component={Link}
                                    to="/contact"
                                    variant="contained"
                                    size="large"
                                    onClick={() => window.scrollTo(0, 0)}
                                    endIcon={<ArrowForwardIcon />}
                                    sx={{
                                        py: 1.75,
                                        px: 4,
                                        backgroundColor: theme.palette.common.white,
                                        color: theme.palette.primary.dark,
                                        '&:hover': {
                                            backgroundColor: alpha('#ffffff', 0.88)
                                        },
                                        fontSize: '1.0625rem',
                                        width: { xs: '100%', sm: 'auto' }
                                    }}
                                >
                                    Contact Us
                                </Button>
                            </Box>
                        </Box>
                    </Reveal>
                </Container>
            </Box>
        </Box>
    );
};

export default About;
